import type {
  GeneratedQuestionsResult,
  AnalysisGroup,
  QuestionGroup,
  OptionItem,
  GenerateContentSearchResponse,
} from "@/types/apis/naniexpert";
import type { OptionLabelsType } from "@/types/common.types";
import { GENERATION_FORMAT, QUESTION_TYPE } from "@/utils/constants";

/**
 * AI 生成結果格式驗證失敗的自訂錯誤
 * 用於區分 API 本身的錯誤與格式不符的情況，以便分類追蹤
 */
export class GenerationFormatError extends Error {
  public readonly invalidRawResponse: string;

  constructor(invalidRawResponse: string, message?: string) {
    super(message ?? "Generation format validation failed");
    this.name = "GenerationFormatError";
    this.invalidRawResponse = invalidRawResponse;
  }
}

/**
 * 從 options 陣列依照 code 取得對應的 name
 */
export const getOptionNameByCode = (
  options: OptionItem[],
  code: string | null
): string => {
  if (!code) return "";
  return options.find((o) => o.code === code)?.name ?? "";
};

/**
 * 解析 generateContentByParams 回傳的 response.geminiResponse.text，並加上顯示用的編號：
 * - 題組標題加編號（questionGroups / analysisGroups）
 * - 子題 questionContent 加編號
 * - 選項加編號
 *    - 國小： ①②③④
 *    - 國中高中：ABCD
 * - analysisGroups 補上 questionContent 編號
 * - correctAnswer 格式化為「編號 + 答案文字」（格式跟選項一樣要看學制）
 */
export const parseGenerateContentResult = (
  response: GenerateContentSearchResponse,
  labels: OptionLabelsType
): GeneratedQuestionsResult => {
  if (!response.geminiResponse?.text) {
    throw new Error(
      "[parseGenerateContentResult] geminiResponse.text is empty, response: " +
        JSON.stringify(response)
    );
  }

  const raw = JSON.parse(
    response.geminiResponse.text
  ) as GeneratedQuestionsResult;

  const questionGroups = raw.questionGroups.map((group, groupIndex) => ({
    ...group,
    groupTitle: `${groupIndex + 1}. ${group.groupTitle}`,
    subQuestions: group.subQuestions.map((subQuestion, subQuestionIndex) => ({
      ...subQuestion,
      questionContent: `(${subQuestionIndex + 1}) ${
        subQuestion.questionContent
      }`,
      options: subQuestion.options.map(
        (option, optionIndex) => `${labels[optionIndex]} ${option}`
      ),
    })),
  }));

  const analysisGroups = raw.analysisGroups.map((group, groupIndex) => ({
    ...group,
    groupTitle: `${groupIndex + 1}. ${group.groupTitle}`,
    subAnalysises: group.subAnalysises.map((subAnalysis, subAnalysisIndex) => {
      const correspondingOptions =
        raw.questionGroups[groupIndex]?.subQuestions[subAnalysisIndex]
          ?.options ?? [];
      const optionIndex = correspondingOptions.indexOf(
        subAnalysis.correctAnswer
      );
      const formattedAnswer =
        subAnalysis.correctAnswer && optionIndex >= 0
          ? `${labels[optionIndex]} ${subAnalysis.correctAnswer}`
          : subAnalysis.correctAnswer;

      return {
        ...subAnalysis,
        questionContent: `(${subAnalysisIndex + 1})`,
        correctAnswer: formattedAnswer ? `正確答案：${formattedAnswer}` : "",
        analysis: `解析：${subAnalysis.analysis}`,
      };
    }),
  }));

  return { questionGroups, analysisGroups };
};

export const buildQuestionText = (group: QuestionGroup): string => {
  const parts: string[] = [];
  parts.push(group.groupTitle);
  if (group.readingText) {
    parts.push(group.readingText);
  }
  group.subQuestions.forEach((sq, index) => {
    parts.push(
      index === 0 ? `${sq.questionContent}` : `\n${sq.questionContent}`
    );
    if (sq.options.length > 0) {
      sq.options.forEach((opt) => {
        parts.push(opt);
      });
    }
  });
  return parts.join("\n");
};

/**
 * 驗證 AI 生成結果是否符合 GENERATION_FORMAT 定義的格式規範
 * - 題組數量
 * - 每題組的子題數量、類型分布
 * - 選擇題選項數量、開放題選項數量
 * - 解析組數量與子解析數量
 */
export const validateGenerationFormat = (
  result: GeneratedQuestionsResult
): boolean => {
  const {
    QUESTION_GROUP_COUNT,
    SUB_QUESTION_COUNT,
    SUB_QUESTION_MULTIPLE_CHOICE_COUNT,
    MULTIPLE_CHOICE_OPTION_COUNT,
  } = GENERATION_FORMAT;

  // 題組數量 & 解析組數量須一致且等於預設值
  if (
    result.questionGroups.length !== QUESTION_GROUP_COUNT ||
    result.analysisGroups.length !== QUESTION_GROUP_COUNT
  ) {
    return false;
  }

  for (const group of result.questionGroups) {
    // 每題組子題總數
    if (group.subQuestions.length !== SUB_QUESTION_COUNT) return false;

    const multipleChoiceQuestions = group.subQuestions.filter(
      (sq) => sq.type === QUESTION_TYPE.MULTIPLE_CHOICE
    );

    // 單選題 & 開放題子題數量
    if (multipleChoiceQuestions.length !== SUB_QUESTION_MULTIPLE_CHOICE_COUNT)
      return false;

    // 單選題選項數量
    for (const sq of multipleChoiceQuestions) {
      if (sq.options.length !== MULTIPLE_CHOICE_OPTION_COUNT) return false;
    }
  }

  // 每個解析組的子解析數量須與子題數量一致
  for (const group of result.analysisGroups) {
    if (group.subAnalysises.length !== SUB_QUESTION_COUNT) return false;
  }

  return true;
};

export const buildAnalysisText = (group: AnalysisGroup): string => {
  const parts: string[] = [];
  parts.push(group.groupTitle);
  group.subAnalysises.forEach((sa, index) => {
    parts.push(
      index === 0 ? `${sa.questionContent}` : `\n${sa.questionContent}`
    );
    if (sa.correctAnswer) {
      parts.push(sa.correctAnswer);
    }
    parts.push(sa.analysis);
  });
  return parts.join("\n");
};
